// src/hooks/useRollen.ts

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { RootState, AppDispatch } from '../redux/store';
import { setRollen, setError, updateRollenStatus } from '../redux/slices/rollenSlice';
import { Rollen, ExpertStatus } from '../models/Rollen';

// Helper function to convert Firestore data
const convertFirestoreData = (doc: any): Rollen => {
  const data = doc.data();

  return {
    id: doc.id,
    ...data,
    createdAt:
      data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt:
      data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  } as Rollen;
};

export const useRollen = (problemId: string, userId: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const rollen = useSelector((state: RootState) => state.rollen.rollen);
  const loading = useSelector((state: RootState) => state.rollen.loading);
  const error = useSelector((state: RootState) => state.rollen.error);

  useEffect(() => {
    const rollenQuery = query(
      collection(db, 'rollen'),
      where('problemId', '==', problemId),
      where('userId', '==', userId)
    );

    const unsubscribe = onSnapshot(
      rollenQuery,
      snapshot => {
        const rollenList: Rollen[] = [];
        snapshot.forEach(doc => {
          rollenList.push(convertFirestoreData(doc));
        });
        dispatch(setRollen(rollenList));
      },
      error => {
        console.error('Error fetching rollen:', error);
        dispatch(setError(error.message));
      }
    );

    return () => unsubscribe();
  }, [dispatch, problemId, userId]);

  // Function to update expert status
  const updateStatus = async (rolleId: string, status: ExpertStatus) => {
    try {
      await dispatch(updateRollenStatus({ rolleId, status })).unwrap();
    } catch (error) {
      console.error('Failed to update expert status:', error);
    }
  };

  return {
    rollen,
    loading,
    error,
    updateStatus,
  };
};
